import { Link } from 'react-router-dom';
import { useBlogPosts, slugify } from '@/hooks/useBlogStore';
import { useNavigate } from 'react-router-dom';

export default function BlogAdmin() {
  const { posts, deletePost } = useBlogPosts();
  const navigate = useNavigate();

  const handleDelete = (slug: string, title: string) => {
    if (window.confirm(`Delete "${title}"? This cannot be undone.`)) {
      deletePost(slug);
    }
  };

  const handleNewPost = () => {
    const slug = slugify('new-post-' + Date.now());
    navigate(`/blog/admin/edit/${slug}`);
  };

  return (
    <main className="bg-background-50 min-h-screen pt-24 md:pt-28">
      <section className="w-full px-4 md:px-6 lg:px-8 py-8 md:py-12">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <span className="text-xs font-semibold uppercase tracking-widest text-primary-600 mb-1 block">
                Admin
              </span>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">
                Blog Posts
              </h1>
              <p className="text-sm text-foreground-600 mt-1">
                {posts.length} {posts.length === 1 ? 'post' : 'posts'} total
              </p>
            </div>
            <button
              type="button"
              onClick={handleNewPost}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-md bg-primary-500 text-white hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-add-line"></i>
              </span>
              New Post
            </button>
          </div>

          {posts.length === 0 ? (
            <div className="text-center py-16 bg-background-50 border border-background-200 rounded-xl">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 text-foreground-400">
                <i className="ri-article-line text-3xl"></i>
              </div>
              <p className="text-foreground-600 mb-4">No blog posts yet. Create your first one!</p>
              <button
                type="button"
                onClick={handleNewPost}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-primary-500 text-white hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Create First Post
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {posts.map((post) => (
                <div
                  key={post.slug}
                  className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 bg-background-50 border border-background-200 rounded-lg hover:border-background-300 transition-colors"
                >
                  <div className="w-full sm:w-20 h-14 shrink-0 rounded-md overflow-hidden bg-background-100">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover object-top"
                      width={80}
                      height={56}
                    />
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-foreground-900 truncate">
                      {post.title}
                    </h3>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      <span className="px-2 py-0.5 text-xs rounded-full bg-primary-100 text-primary-800">
                        {post.category}
                      </span>
                      <span className="text-xs text-foreground-500">{post.readTime}</span>
                      <span className="text-xs text-foreground-400">{post.publishedAt}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    <Link
                      to={`/blog/admin/edit/${post.slug}`}
                      className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer"
                      title="Edit post"
                    >
                      <i className="ri-edit-line text-sm"></i>
                    </Link>
                    <a
                      href={`/blog/${post.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer"
                      title="Preview live post"
                    >
                      <i className="ri-external-link-line text-sm"></i>
                    </a>
                    <button
                      type="button"
                      onClick={() => handleDelete(post.slug, post.title)}
                      className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-red-50 text-foreground-500 hover:text-red-600 transition-colors cursor-pointer"
                      title="Delete post"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}